import { Fragment, HTMLProps, useEffect, useRef, useState } from "react";

import date from "date-and-time";
import { useNavigate } from "react-router-dom";
import { DateRangeType } from "react-tailwindcss-datepicker";

import { CameraIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { StarIcon } from '@heroicons/react/24/solid';
import { createColumnHelper, flexRender, getCoreRowModel, getExpandedRowModel, getPaginationRowModel, getSortedRowModel, RowModel, SortingState, useReactTable } from "@tanstack/react-table";

import useVehiclesMutations from "../../data/useVehiclesMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Modal from "../../layouts/Modal";
import { PageMode } from "../../models/PagedTable";
import { IVehicle, PartsStatusDropdowns, PartsStatusValues } from "../../models/Vehicle";
import openInNewTab from "../../utils/openInNewTab";
import PriceFormat from "../../utils/priceFormat";
import PagedTable from "../shared/Tables/PagedTable";
import VehicleIcons from "../shared/VehicleIcons/VehicleIcons";
import VehicleLocationDropdown from "../vehicles/VehicleLocationDropdown";
import EnginesWorldWideOffers from "../vehiclesAwaitingArrival/EnginesWorldWideOffers";
import AwaitingInventoryEbayPrice from "./AwaitingInventoryEbayPrice";
import AwaitingInventoryPrice from "./AwaitingInventoryPrice";
import AwaitingInventoryStatusDropdown from "./AwaitingInventoryStatusDropdown";
import AwaitingInventoryVfePrice from "./AwaitingInventoryVfePrice";
import CustomDatePicker from "./CustomDatePicker";
import PartsPriorityDropdown from "./PartsPriorityDropdown";
import YardStatusDropdown from "./YardStatusDropdown";

import useCustomerDetails from "../../data/useCustomerDetails";
import LoadingWheel from "../shared/LoadingWheel";
import { FeatureStatus } from "../../models/Customer";

function IndeterminateCheckbox({
  indeterminate,
  className = '',
  ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
  const ref = useRef<HTMLInputElement>(null!)

  useEffect(() => {
    if (typeof indeterminate === 'boolean') {
      ref.current.indeterminate = !rest.checked && indeterminate
    }
  }, [ref, indeterminate])

  return (
    <input
      type="checkbox"
      ref={ref}
      className={className + 'p-2 cursor-pointer'}
      {...rest}
    />
  )
}

const colHelper = createColumnHelper<IVehicle>();

const cols = [
  {
    id: 'select',
    header: ({ table }: any) => (
      <IndeterminateCheckbox
        {...{
          checked: table.getIsAllRowsSelected(),
          indeterminate: table.getIsSomeRowsSelected(),
          onChange: table.getToggleAllRowsSelectedHandler(),
        }}
      />
    ),
    cell: ({ row }: any) => (
      <div className="flex justify-center items-center flex-1 p-1" onClick={(e) => e.stopPropagation()}>
        <IndeterminateCheckbox
          {...{
            id: row.original.id,
            checked: row.getIsSelected(),
            disabled: !row.getCanSelect(),
            indeterminate: row.getIsSomeSelected(),
            onChange: row.getToggleSelectedHandler(),
          }}
        />
        <button
          onClick={(e) => { e.stopPropagation(); row.toggleExpanded(); }}
          style={{ transform: `rotate(${!row.getIsExpanded() ? "0" : "-270"}deg)` }}
          className="ml-3 p-1 transition duration-500 cursor-pointer border border-gray-400 rounded-full hover:bg-gray-200"
        >
          {/* {row.getIsExpanded() ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4"/>} */}
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </div>
    ),
  },
  colHelper.display({
    id: "image",
    cell: (info) => (
      <span className="flex justify-center relative" >
        {info.row.original?.vehicleImageUrl ? (
          <>
            <img
              className="w-32 h-20 object-contain"
              src={`${info.row.original != null ? info.row.original.vehicleImageUrl : null}`}
            />
            {info.row.original?.vehiclePremium && (
              <StarIcon className="w-8 h-8 text-amber-500 absolute -top-2 -right-2" />
            )}

          </>
        ) : (
          <CameraIcon className="w-14 text-gray-600" />
        )}
      </span>
    ),
  }),
  colHelper.accessor("combinedMake", {
    id: "combinedMake",
    header: "Make & Model",
    enableSorting: false,
    cell: (info) => <div>
      <p>{info.row.original?.combinedMake}</p>
      <p>{info.row.original?.combinedModel}</p>
    </div>,
  }),
  colHelper.accessor("vrm", {
    id: "vrm",
    header: "VRM",
    enableSorting: false,
  }),
  colHelper.accessor("engineModelCode", {
    id: "engineModelCode",
    header: "Engine Code",
    enableSorting: false,
  }),
  colHelper.accessor("bodyStyleDescription", {
    id: "bodyStyleDescription",
    header: "Mileage & Body Type",
    enableSorting: false,
    cell: (info) => <div>
      <p>{info.row.original?.odometerInMiles}</p>
      <p>{info.row.original?.bodyStyleDescription}</p>
    </div>,
  }),
  // colHelper.accessor("vehicleStarting", {
  //   header: "Starting",
  //   enableSorting: false,
  //   cell: (info) => <span className="flex justify-center text-xl">{info.getValue() ? "✔" : "✘"}</span>,
  // }),
  colHelper.display({
    id: "vehicle",
    header: "Vehicle",
    enableSorting: false,
    cell: (info) => <VehicleIcons vehicle={info.row.original!} />,
  }),
  colHelper.accessor("valuationTradePoor", {
    id: "valuationTradePoor",
    header: "Salvage",
    enableSorting: false,
    cell: (info) => <span>{PriceFormat(info.row.original?.valuationTradePoor)}</span>,
  }),
  colHelper.accessor("ebayEnginePrice", {
    id: "ebayEnginePrice",
    header: "Ebay Engine £",
    cell: (info) => <AwaitingInventoryEbayPrice vehicle={info.row.original} />,
  }),
  colHelper.accessor("enginesWorldwideEngineOffer", {
    id: "enginesWorldwideEngineOffer",
    header: "Export Offers £",
    cell: (info) => <div>
      <p>Engine Offer</p>
      <AwaitingInventoryPrice vehicle={info.row.original!} />
      <p className="mt-2 ">VFE Offer</p>
      <AwaitingInventoryVfePrice vehicle={info.row.original!} />
    </div>
  }),
  colHelper.accessor("enginesWorldwide", {
    id: "enginesWorldwide",
    header: "Export Status",
    sortingFn: "auto",
    cell: (info) => <EnginesWorldWideOffers offers={info.getValue()} />
  }),
  colHelper.accessor("scheduledCollectionOn", {
    id: "scheduledCollectionOn",
    header: "Collection",
    sortingFn: "auto",
    cell: (collection) => <span>{collection.getValue() !== null ? date.format(new Date(collection.getValue()!), "DD/MM/YYYY") : "Not Set"}</span>,
  }),
  colHelper.accessor("yardStatus", {
    id: "yardStatus",
    header: "Status",
    enableSorting: false,
    cell: (info) => <div id={info.row.original.id}>
      <p>Parts Status</p>
      <AwaitingInventoryStatusDropdown vehicle={info.row.original!} classes="h-8 w-40 text-sm" />
      <p className="mt-2">Location</p>
      <VehicleLocationDropdown vehicle={info.row.original!} classes="h-8 w-40 text-sm" />
      <p className="mt-2 ">Yard Status</p>
      <YardStatusDropdown vehicle={info.row.original!} classes="h-8 w-40 text-sm" />
      <p className="mt-2">Parts Priority Status</p>
      <PartsPriorityDropdown vehicle={info.row.original!} classes="h-8 w-40 text-sm" />
    </div>
  }),
];


export default function AwaitingInventoryTable() {
  const navigate = useNavigate();
  const { bulkUpdate } = useVehiclesMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Leads");
  const [updateOpen, setUpdateOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("0");

  const [hideUnscheduled, setHideUnscheduled] = useState(true);
  const [hideNotCollected, setHideNotCollected] = useState(false);
  const [hideCompleted, setHideCompleted] = useState(false);
  const [hideNoLeads, setHideNoLeads] = useState(true);
  const [currentFilter, setCurrentFilter] = useState("0");

  const [toUpdate, setToUpdate] = useState<RowModel<IVehicle> | undefined>(undefined);
  const [additionalBody, setAdditionalBody] = useState<Record<string, any>>({});

  const { features } = useCustomerDetails(); //Lead Management
  const [leadManagementEnabled, setLeadManagementEnabled] = useState(false);

  const [dates, setDates] = useState<DateRangeType>({
    startDate: null,
    endDate: null
  });

  const handleDatesChange = (newDates: any) => {
    setDates(newDates);
  };

  const filters = [
    ...Object.values(PartsStatusDropdowns).filter((ct) => isNaN(Number(ct))),
    "All"
  ];

  const handleRowUpdate = () => {
    if(toUpdate == undefined) return;

    var vehicleIds = toUpdate.rows.map((row) => row.original?.id);

    const body = {
      engineExportStatus: parseInt(selectedStatus as keyof PartsStatusValues),
      vehicles: vehicleIds
    };

    bulkUpdate.mutate(body, {
      onSuccess: () => {
        saveHandlers.onSuccess();
        setUpdateOpen(false);
      },
      onError: saveHandlers.onError
    });
  }

  useEffect(() => {
    var numericValue: number|null = 0;
    
    if (currentFilter == "All" ){
      numericValue = null;
    } else if (!isNaN(Number(currentFilter))){
      numericValue = parseInt(currentFilter as keyof PartsStatusValues);
    } else{
      numericValue = PartsStatusValues[currentFilter as keyof typeof PartsStatusValues];
    }

    setAdditionalBody({
      includeCompleted   : !hideCompleted,
      includeCancelled   : !hideNotCollected,
      includeUnscheduled : !hideUnscheduled,
      includeNoLeads     : !hideNoLeads,
      startDate          : dates.startDate?.toISOString(),
      endDate            : dates.endDate?.toISOString(),
      engineExportStatus : currentFilter === "All" ? null : numericValue,
    })
  }, [currentFilter, dates, hideUnscheduled, hideNotCollected, hideCompleted, hideNoLeads]);

  useEffect(() => {
    if (features.data && !features.isLoading) {
      setLeadManagementEnabled(features.data.data.find(f => f.name === "Lead Management" && f.status == FeatureStatus.Enabled) ? true : false);
    }
  }, [features.data, features.isLoading]);

  if (features.isLoading) return <LoadingWheel />;

  return (
    <>
      {/* ---------- Modal ---------- */}
      <Modal width="max-w-2xl" open={updateOpen} setOpen={setUpdateOpen} >
        <h1 className="text-xl">Bulk Update</h1>
        <div className="mt-4">
          <p>Change all selected engines to which status?</p>
          <div>
            <select
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="mt-2 border border-gray-400 rounded-md text-sm"
            >
              {Object.values(PartsStatusValues).filter((ct) => isNaN(Number(ct))).map((status, i) => (
                <option key={status} value={i}>
                  {status}
                </option>
              ))}
            </select>
          </div>
          <button
            className="mt-5 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700"
            onClick={handleRowUpdate}
          >
            Update
          </button>
        </div>
      </Modal>
      {/* ---------- Search ---------- */}
      <div className="mt-4 flex justify-between">
        <div className="flex gap-x-5">
          <select aria-label="Contact type filter"
            onChange={(e) => setCurrentFilter(e.target.value)}
            className="sm:text-sm">
            {filters.map((filter) => (
              <option
                className="sm:text-sm"
                key={filter}>
                {String(filter)}
              </option>
            ))}
          </select>

          <div>
            <CustomDatePicker dates={dates} handleDatesChange={handleDatesChange} />
          </div>

        </div>
        <div className="flex items-center gap-x-4">
          <div className="flex items-center">
            <input id="hideUnscheduled" onChange={(e) => setHideUnscheduled(e.target.checked)} value="hideUnscheduled" checked={hideUnscheduled} type="checkbox" className="mr-2 h-5 w-5" />
            <label htmlFor="hideUnscheduled">Hide Unscheduled</label>
          </div>
          <div className="flex items-center">
            <input id="hideNotCollected" onChange={(e) => setHideNotCollected(e.target.checked)} value="hideNotCollected" checked={hideNotCollected} type="checkbox" className="mr-2 h-5 w-5" />
            <label htmlFor="hideNotCollected">Hide Not Collected</label>
          </div>
          {leadManagementEnabled && (
            <>
              <div className="flex items-center">
                <input id="hideCompleted" onChange={(e) => setHideCompleted(e.target.checked)} value="hideCompleted" checked={hideCompleted} type="checkbox" className="mr-2 h-5 w-5" />
                <label htmlFor="hideCompleted">Hide Completed</label>
              </div>
{/* 
              <div className="flex items-center">
                <input id="hideNoLeads" onChange={(e) => setHideNoLeads(e.target.checked)} value="hideNoLeads" checked={hideNoLeads} type="checkbox" className="mr-2 h-5 w-5" />
                <label htmlFor="hideNoLeads">Hide No Leads</label>
              </div> */}
            </>
          )}
        </div>
      </div>

      <PagedTable
        key="awaiting-inventory"
        url="/vehicle/partial"
        
        columns={cols}
        pageMode={PageMode.Stock} // does nothing for this table
        queryLabel="Search any vehicle details"
        onRowClick={(row: IVehicle) => {
          if (leadManagementEnabled && row.leads.length > 0) {
            openInNewTab(`/leads/${row.leads[0].id}`);
          } else {
            openInNewTab(`/vehicles/${row.id}`);
          }
        }}
        actionButtons={[
          { label: "Bulk Update", onClick: (rows: RowModel<IVehicle>) => {setToUpdate(rows); setUpdateOpen(true)}, disabledOnNoRows: true },
          { label: "Vehicle Lookup", onClick: () => navigate("/parts-manager/vehicle-lookup"), disabledOnNoRows: false },
        ]} // TODO: add action buttons here
        showTotal={true}
        additionalBody={additionalBody}
        // set the initial sort (SortingState) to scheduledCollectionOn
        initialSort={[{ id: "scheduledCollectionOn", desc: true }]}
        expandedRow={(vehicle) => (
          <div className="flex gap-x-10 p-4">

            <div className="flex flex-col">
              <span className="font-bold">Vehicle Notes:</span>
              <span>{vehicle.notes}</span>
            </div>

            <div className="flex flex-col">
              <span className="font-bold">Office Notes:</span>
              <span>{vehicle.leads[0]?.officeNotes}</span>
            </div>

            <div className="flex flex-col">
              <span className="font-bold">Driver Notes:</span>
              <span>{vehicle.leads[0]?.driverNotes}</span>
            </div>

          </div>
        )}
        rowColor={(row) => leadManagementEnabled && row.leads.length == 0 ? 'bg-primary-50 hover:bg-primary-100' : ''}
      />

    </>
  );
}